import type {
  AgentDefinition,
  AgentInit,
  AgentProfile,
  AgentRunContext,
  DefinedAgent,
  FabricAgent,
  FabricSession,
  JsonObject,
  ModelProvider,
  SandboxBackend,
  ToolDef,
} from "@fabric-harness/sdk";
import {
  defineAgent,
  listSandboxBackendFactories,
  mergeCapabilityPolicies,
} from "@fabric-harness/sdk/strict";
import { databricksTenantCostLimit } from "./cost-budget-actuals.js";
import { analyticsCopilotGovernance } from "./governance.js";
import { databricksPrincipalFromEnv } from "./identity.js";
import type {
  DatabricksBundle,
  DatabricksBundleConfig,
  DatabricksManagedMcpBundle,
} from "./index.js";
import { databricks, databricksWithManagedMcp } from "./index.js";
import { registerDatabricksSqlSandboxBackend } from "./sql-sandbox.js";

export type DefineDatabricksAgentOptions<TInput = JsonObject, TOutput = unknown> = Omit<
  AgentDefinition<TInput, TOutput>,
  "run" | "tools"
> & {
  databricks?: DatabricksBundleConfig;
  /** Configure the governed Genie + SELECT-only SQL + cost-reporting product spine from env. */
  analyticsCopilot?: boolean;
  modelProvider?: ModelProvider;
  tools?: Array<ToolDef | string>;
  sandbox?: SandboxBackend;
  profile?: AgentProfile;
  run?: (
    context: AgentRunContext<TInput> & { databricks: DatabricksBundle },
  ) => Promise<TOutput> | TOutput;
};

function mergeProfile(options: { profile?: AgentProfile } | undefined): Partial<AgentProfile> {
  const profile = options?.profile;
  if (!profile) return {};
  return {
    ...(profile.name !== undefined ? { name: profile.name } : {}),
    ...(profile.description !== undefined ? { description: profile.description } : {}),
    ...(profile.instructions !== undefined ? { instructions: profile.instructions } : {}),
    ...(profile.model !== undefined ? { model: profile.model } : {}),
    ...(profile.skills !== undefined ? { skills: profile.skills } : {}),
    ...(profile.tools !== undefined ? { tools: profile.tools } : {}),
    ...(profile.policy !== undefined ? { policy: profile.policy } : {}),
    ...(profile.costBudget !== undefined ? { costBudget: profile.costBudget } : {}),
    ...(profile.approvals !== undefined ? { approvals: profile.approvals } : {}),
    ...(profile.sandbox !== undefined ? { sandbox: profile.sandbox } : {}),
  };
}

function mergeOptions<TInput, TOutput>(
  options: DefineDatabricksAgentOptions<TInput, TOutput>,
): DefineDatabricksAgentOptions<TInput, TOutput> {
  const profile = mergeProfile(options);
  return {
    ...profile,
    ...options,
    ...(profile.skills || options.skills
      ? { skills: [...(profile.skills ?? []), ...(options.skills ?? [])] }
      : {}),
    ...(profile.tools || options.tools
      ? { tools: [...(profile.tools ?? []), ...(options.tools ?? [])] }
      : {}),
  };
}

function bindDatabricksContext<TInput>(
  context: AgentRunContext<TInput>,
  bundle: DatabricksBundle,
  init: (options?: AgentInit) => Promise<FabricAgent>,
  defaultInit?: AgentInit,
): AgentRunContext<TInput> & { databricks: DatabricksBundle } {
  let agentPromise: Promise<FabricAgent> | undefined;
  let sessionPromise: Promise<FabricSession> | undefined;
  const getAgent = () => {
    agentPromise ??= init(defaultInit);
    return agentPromise;
  };
  const session = (id?: string, options?: Parameters<FabricAgent["session"]>[1]) => {
    if (id === undefined && options === undefined) {
      sessionPromise ??= getAgent().then((agent) => agent.session());
      return sessionPromise;
    }
    return getAgent().then((agent) => agent.session(id, options));
  };
  return {
    ...context,
    databricks: bundle,
    init,
    session,
    prompt: async (text, options) => (await session()).prompt(text, options),
    skill: async (name, options) => (await session()).skill(name, options),
    task: async (text, options) => (await session()).task(text, options),
    shell: async (command, options) => (await session()).shell(command, options),
  };
}

export function defineDatabricksAgent<TInput = JsonObject, TOutput = unknown>(
  options: DefineDatabricksAgentOptions<TInput, TOutput> = {},
): DefinedAgent<TInput, TOutput> {
  const merged = options ? mergeOptions(options) : {};
  const {
    databricks: explicitConfig,
    analyticsCopilot = false,
    modelProvider,
    tools = [],
    sandbox,
    profile: _profile,
    run,
    ...definition
  } = merged;
  const directTools = tools.filter((tool): tool is ToolDef => typeof tool !== "string");
  const toolRefs = tools.filter((tool): tool is string => typeof tool === "string");
  if (sandbox === "databricks" && !listSandboxBackendFactories().includes("databricks")) {
    registerDatabricksSqlSandboxBackend();
  }
  const def: AgentDefinition<TInput, TOutput> = {
    ...definition,
    ...(directTools.length > 0 ? { tools: directTools } : {}),
    run: async (context) => {
      const env = context.env ?? {};
      const baseConfig = explicitConfig ?? databricksConfigFromEnv(env, { analyticsCopilot });
      // Selecting the read-only SQL tool is itself explicit consent to expose that narrow verb.
      // A warehouse id remains connectivity only and never enables arbitrary SQL execution.
      const config =
        baseConfig.warehouseId &&
        !baseConfig.sqlRead &&
        toolRefs.some((ref) => ref === "sql" || ref === "sql-read" || ref === "sql_read")
          ? { ...baseConfig, sqlRead: true }
          : baseConfig;
      const bundle = config.mcp?.length
        ? await databricksWithManagedMcp(config)
        : databricks(config);
      try {
        const analyticsCostLimit = analyticsCopilot
          ? databricksAnalyticsCostLimitFromEnv(env, config, bundle)
          : undefined;
        if (toolRefs.length > 0) resolveToolRefs(bundle, toolRefs);
        const directNames = new Set(directTools.map((tool) => tool.name));
        const bundleTools = bundle.tools.filter((tool) => !directNames.has(tool.name));
        const providerInit = (initOptions: AgentInit = {}) => {
          const policy = mergeCapabilityPolicies(bundle.policy, initOptions.policy);
          return context.init({
            ...initOptions,
            modelProvider: initOptions.modelProvider ?? modelProvider ?? bundle.modelProvider,
            tools: [...bundleTools, ...(initOptions.tools ?? [])],
            ...(analyticsCostLimit && initOptions.costLimit === undefined
              ? { costLimit: analyticsCostLimit }
              : {}),
            ...(policy ? { policy } : {}),
            ...(bundle.store && initOptions.store === undefined ? { store: bundle.store } : {}),
            ...(sandbox && initOptions.sandbox === undefined ? { sandbox } : {}),
          });
        };
        const providerContext = bindDatabricksContext(
          context,
          bundle,
          providerInit,
          definition.init,
        );
        if (run) return await run(providerContext);
        const question =
          (context.input as { question?: string })?.question ?? "What would you like to know?";
        return (await providerContext.prompt(question)) as TOutput;
      } finally {
        if ("managedMcp" in bundle) {
          await (bundle as DatabricksManagedMcpBundle).managedMcp.close();
        }
      }
    },
  };
  return defineAgent(def);
}

function databricksAnalyticsCostLimitFromEnv(
  env: Record<string, string | undefined>,
  config: DatabricksBundleConfig,
  bundle: DatabricksBundle,
) {
  if (env.FABRIC_MOCK === "1") return undefined;
  const warehouseId = config.warehouseId;
  const tenantId = env.DATABRICKS_COST_TENANT_ID?.trim();
  const perDayUsd = Number(env.DATABRICKS_COST_PER_DAY_USD);
  if (!warehouseId) throw new Error("Analytics copilot cost enforcement requires a SQL Warehouse.");
  if (!tenantId) throw new Error("Analytics copilot requires DATABRICKS_COST_TENANT_ID.");
  if (!Number.isFinite(perDayUsd) || perDayUsd <= 0) {
    throw new Error("Analytics copilot requires a positive DATABRICKS_COST_PER_DAY_USD.");
  }
  return databricksTenantCostLimit(bundle.sdk.statements, warehouseId, tenantId, {
    perDayUsd,
    onExceed: "throw",
  });
}

function databricksConfigFromEnv(
  env: Record<string, string | undefined>,
  options: { analyticsCopilot?: boolean } = {},
): DatabricksBundleConfig {
  const rawHost = env.DATABRICKS_HOST;
  const host = rawHost ? (rawHost.includes("://") ? rawHost : `https://${rawHost}`) : undefined;
  if (!host && env.FABRIC_MOCK !== "1") {
    throw new Error(
      "Missing DATABRICKS_HOST environment variable. Set it or pass an explicit databricks config.",
    );
  }
  const principal =
    env.FABRIC_MOCK === "1"
      ? ({ kind: "pat", token: "mock-token" } as const)
      : databricksPrincipalFromEnv(env);
  const analyticsCopilot = options.analyticsCopilot === true;
  const genieAgentId = env.DATABRICKS_GENIE_SPACE_ID;
  const stewardAudience = env.DATABRICKS_ANALYTICS_STEWARD_AUDIENCE;
  if (analyticsCopilot && env.FABRIC_MOCK !== "1" && !env.DATABRICKS_WAREHOUSE_ID) {
    throw new Error("Analytics copilot requires DATABRICKS_WAREHOUSE_ID.");
  }
  if (analyticsCopilot && env.FABRIC_MOCK !== "1" && !genieAgentId) {
    throw new Error("Analytics copilot requires DATABRICKS_GENIE_SPACE_ID.");
  }
  if (analyticsCopilot && env.FABRIC_MOCK !== "1" && !stewardAudience) {
    throw new Error("Analytics copilot requires DATABRICKS_ANALYTICS_STEWARD_AUDIENCE.");
  }
  const catalog = env.DATABRICKS_CATALOG?.trim();
  return {
    host: host ?? "https://mock.databricks.com",
    principal,
    ...(env.DATABRICKS_MODEL ? { model: env.DATABRICKS_MODEL } : {}),
    ...(env.DATABRICKS_WAREHOUSE_ID ? { warehouseId: env.DATABRICKS_WAREHOUSE_ID } : {}),
    ...(env.FABRIC_MOCK === "1" ? { warehouseId: "mock-warehouse-id" } : {}),
    ...(analyticsCopilot
      ? {
          sqlRead: true,
          consumption: true,
          genie: {
            conversations: {
              agentId: genieAgentId ?? "mock-genie-agent",
            },
          },
          governance: analyticsCopilotGovernance({
            stewardAudience: stewardAudience ?? "analytics-stewards",
            ...(catalog ? { catalogs: [catalog] } : {}),
          }),
        }
      : {}),
  };
}

export function resolveToolRefs(bundle: DatabricksBundle, refs: string[]): ToolDef[] {
  const toolMap = new Map<string, ToolDef>();
  for (const tool of bundle.tools) {
    toolMap.set(tool.name, tool);
  }

  const shortNameMap: Record<string, string> = {
    sql: "sql_read",
    "sql-read": "sql_read",
    tables: "databricks_unity_catalog_tables",
    "table-info": "databricks_table_info",
    search: "search",
    "ai-query": "databricks_ai_query",
    genie: "databricks_genie_ask",
    "lakeflow-list": "databricks_pipeline_list",
    "lakeflow-status": "databricks_pipeline_status",
    "lakeflow-start": "databricks_pipeline_start",
    "lakeflow-stop": "databricks_pipeline_stop",
    consumption: "databricks_consumption",
    "feature-lookup": "databricks_feature_lookup",
  };

  const result: ToolDef[] = [];
  for (const ref of refs) {
    const fullName = shortNameMap[ref] ?? ref;
    const tool = toolMap.get(fullName);
    if (!tool) {
      const available = [...toolMap.keys()].join(", ");
      throw new Error(`Unknown tool reference "${ref}". Available tools: ${available}`);
    }
    result.push(tool);
  }
  return result;
}
